export interface TestConfigEntry {
  target: string;
  variant: string;
}

export interface WidgetInitResponse {
  store_id: string;
  api_base: string;
  theme_css_url?: string;
  token?: string;
  flags?: Record<string, boolean>;
  test_session_id?: string;
  test_config?: TestConfigEntry[];
}

export interface WidgetConfig {
  storeId: string;
  apiBase: string; // full chat endpoint, e.g. https://wandway-backend.onrender.com/chat
  themeCssUrl?: string;
  /**
   * Optional product context for product-page embeds. Platform adapters may set this
   * prior to widget boot so the QA widget can include it as `current_product`.
   */
  currentProductId?: string;
  /**
   * Optional SKU context for stores where URL-derived SKU is the stable PDP identifier.
   * Centra QA requests use this as `current_sku`.
   */
  currentSku?: string;
  /**
   * Optional localisation context, typically set by the store adapter.
   * - language: two-letter language code (e.g., "sv", "en")
   * - market: two-letter market code (e.g., "se", "no")
   */
  language?: string;
  market?: string;
  /** @deprecated Token should never be stored in config or DOM. Use refreshToken callback instead. */
  token?: string;
  instanceId?: string;
  testSessionId?: string;
  testConfig?: TestConfigEntry[];
  /**
   * Feature flags for optional behaviors.
   * - loggingEnabled: Enable chat logging to sessionStorage (default: false for privacy)
   */
  flags?: Record<string, boolean>;
}

declare global {
  interface Window {
    __WW__?: WidgetConfig; // Global default config set by loader
    Wandway?: {
      on: (event: string, handler: (e: CustomEvent) => void) => void;
      off: (event: string, handler: (e: CustomEvent) => void) => void;
      emit: (event: string, detail?: unknown) => void;
      q?: Array<unknown>;
    };
  }
}

const TEST_SESSION_ID_KEY = "ww:test_session_id";
const TEST_SESSION_TS_KEY = "ww:test_session_id_ts";
const TEST_CONFIG_KEY = "ww:test_config";
const TEST_SESSION_TTL_MS = 1000 * 60 * 60 * 48; // 48 hours

function clearStoredTestSession(): void {
  try {
    localStorage.removeItem(TEST_SESSION_ID_KEY);
    localStorage.removeItem(TEST_SESSION_TS_KEY);
    localStorage.removeItem(TEST_CONFIG_KEY);
  } catch {
    // Ignore storage errors
  }
  updateGlobalTestConfig({ testSessionId: null, testConfig: null });
}

export function readStoredTestSessionId(): string | null {
  try {
    const id = localStorage.getItem(TEST_SESSION_ID_KEY);
    if (!id) return null;
    const rawTs = localStorage.getItem(TEST_SESSION_TS_KEY);
    const ts = rawTs ? Number(rawTs) : NaN;
    if (!Number.isFinite(ts)) {
      clearStoredTestSession();
      return null;
    }
    if ((Date.now() - ts) > TEST_SESSION_TTL_MS) {
      clearStoredTestSession();
      return null;
    }
    return id;
  } catch {
    return null;
  }
}

export function persistTestSessionId(id?: string | null): void {
  try {
    if (typeof id === "undefined") return;
    if (!id) {
      clearStoredTestSession();
      return;
    }
    localStorage.setItem(TEST_SESSION_ID_KEY, String(id));
    localStorage.setItem(TEST_SESSION_TS_KEY, String(Date.now()));
  } catch {
    // Ignore storage errors
  }
}

export function readStoredTestConfig(): TestConfigEntry[] | null {
  try {
    const raw = localStorage.getItem(TEST_CONFIG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed as TestConfigEntry[];
  } catch {
    return null;
  }
}

export function getVariantForTarget(target: string): string | null {
  if (!target) return null;
  const sessionId = readStoredTestSessionId();
  if (!sessionId) return null;
  const config = (window.__WW__?.testConfig || readStoredTestConfig()) || null;
  if (!Array.isArray(config)) return null;
  const match = config.find((entry) => entry && entry.target === target);
  return match?.variant ?? null;
}

export function persistTestConfig(config?: TestConfigEntry[] | null): void {
  try {
    if (typeof config === "undefined") return;
    if (!config || !Array.isArray(config) || config.length === 0) {
      localStorage.removeItem(TEST_CONFIG_KEY);
      updateGlobalTestConfig({ testConfig: null });
      return;
    }
    localStorage.setItem(TEST_CONFIG_KEY, JSON.stringify(config));
  } catch {
    // Ignore storage errors
  }
}

export function appendTestSessionId(url: string): string {
  const id = readStoredTestSessionId();
  if (!id) return url;
  try {
    const parsed = new URL(url, window.location.href);
    if (!parsed.searchParams.get("test_session_id")) {
      parsed.searchParams.set("test_session_id", id);
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

export function updateGlobalTestConfig(next: {
  testSessionId?: string | null;
  testConfig?: TestConfigEntry[] | null;
}): void {
  try {
    const g = (window as any).__WW__ || {};
    const nextConfig: WidgetConfig = { ...g };
    if (next.testSessionId === null) {
      delete nextConfig.testSessionId;
    } else if (next.testSessionId) {
      nextConfig.testSessionId = next.testSessionId;
    }
    if (next.testConfig === null) {
      delete nextConfig.testConfig;
    } else if (next.testConfig && Array.isArray(next.testConfig)) {
      nextConfig.testConfig = next.testConfig;
    }
    (window as any).__WW__ = nextConfig;
  } catch {
    // Ignore global update errors
  }
}

export function computeThemeUrl(storeId: string): string {
  // Default CDN origin for widget themes
  return `https://wandway-front-dev.vercel.app/widget/v1/themes/${storeId}.css`;
}

/**
 * Security: Validate theme CSS URLs to prevent loading untrusted or mixed-content stylesheets.
 * Only allows HTTPS URLs from approved CDN hostnames.
 * 
 * @param url - The theme CSS URL to validate
 * @returns true if the URL is safe to load, false otherwise
 */
export function isValidThemeCssUrl(url: string | undefined | null): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;

  try {
    const parsed = new URL(trimmed, window.location.href);
    
    // Security: Only allow HTTPS to prevent mixed content warnings and interception
    if (parsed.protocol !== 'https:') {
      console.warn(
        '[Wandway Security] Theme CSS URL rejected: only HTTPS is allowed.',
        'Provided:', trimmed
      );
      return false;
    }

    // Allowlist of trusted CDN hostnames
    // Add custom domains here as needed (e.g., customer CDNs)
    const allowedHosts = [
      'cdn.pakolabs.com',
      'wandway-front.vercel.app',
      'wandway-front-dev.vercel.app' // temporary fallback during migration
      // Future: Add pattern matching for customer subdomains if needed
      // e.g., /^[\w-]+\.wandway-cdn\.com$/
    ];

    const hostname = parsed.hostname.toLowerCase();
    const isAllowed = allowedHosts.some(allowed => {
      // Exact match or subdomain match
      return hostname === allowed || hostname.endsWith('.' + allowed);
    });

    if (!isAllowed) {
      console.warn(
        '[Wandway Security] Theme CSS URL rejected: hostname not in allowlist.',
        'Hostname:', hostname,
        'Allowed:', allowedHosts
      );
      return false;
    }

    return true;
  } catch (error) {
    console.warn(
      '[Wandway Security] Theme CSS URL rejected: invalid URL format.',
      'Provided:', trimmed
    );
    return false;
  }
}

/**
 * Get the backend base URL, supporting environment variable override for development
 * @returns The backend base URL (without trailing slash)
 */
export function getBackendBaseUrl(): string {
  const envUrl = import.meta.env.VITE_BACKEND_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim().length > 0) {
    const cleanUrl = envUrl.trim().replace(/\/$/, '');
    return cleanUrl.replace(/^http:\/\//i, 'https://');
  }
  return 'https://wandway-backend.onrender.com';
}

/**
 * Get the full widget init endpoint URL
 * @returns The widget init endpoint URL
 */
export function getInitEndpoint(): string {
  return `${getBackendBaseUrl()}/widget/init`;
}


